import React, { useMemo } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { GetPaymentLinksQuery } from '@/types';
import { ListItemSeparator, ListOption } from '@/components/list-item';
import {
  ListSection,
  RecyclerList,
  Props as RecyclerListProps,
} from '@/components/recycler-list';
import { Spacing } from '@usepepper/sprout-kit';

const ESTIMATED_LIST_ITEM_HEIGHT = 70;

export type PaymentLink =
  GetPaymentLinksQuery['getPaymentLinks']['payment_links'][0];

export type PaymentLinkListOption = ListOption<{ paymentLink: PaymentLink }>;

type RecyclerListPropsWithoutRequired = Omit<
  RecyclerListProps<PaymentLinkListOption>,
  'estimatedItemSize' | 'sections'
>;

interface Props extends RecyclerListPropsWithoutRequired {
  listSeparatorSpacing?: Spacing;
  paymentLinks: GetPaymentLinksQuery['getPaymentLinks']['payment_links'];
  renderListItem: (paymentLink: PaymentLink) => React.ReactElement | null;
  selectedPaymentLink?: PaymentLink | null;
}

export const PaymentLinksList: React.FC<Props> = ({
  listSeparatorSpacing,
  paymentLinks,
  renderListItem,
  selectedPaymentLink,
  ...rest
}) => {
  const [styles, theme] = useStyles();

  const sections = useMemo<ListSection<PaymentLinkListOption>[]>(() => {
    return [
      {
        data: (paymentLinks || []).map<PaymentLinkListOption>(
          (paymentLink) => ({
            metadata: {
              paymentLink: paymentLink,
            },
            title: paymentLink.id,
            value: paymentLink.id,
          }),
        ),
        key: null,
      },
    ];
  }, [paymentLinks]);

  return (
    <RecyclerList
      estimatedItemSize={ESTIMATED_LIST_ITEM_HEIGHT}
      extraData={{ selectedPaymentLinkUUID: selectedPaymentLink?.id }}
      ItemSeparatorComponent={() => (
        <ListItemSeparator
          style={[
            styles.listItemSeparator,
            ...(listSeparatorSpacing
              ? [{ marginHorizontal: theme.sk.spacing[listSeparatorSpacing] }]
              : []),
          ]}
        />
      )}
      renderItem={({ item }) => {
        const paymentLink = item.metadata?.paymentLink;
        if (!paymentLink) {
          return null;
        }
        return renderListItem(paymentLink);
      }}
      sections={sections}
      testID="payment-links-list"
      {...rest}
    />
  );
};

const useStyles = createStyles((theme) => ({
  listItemSeparator: {
    backgroundColor: theme.sk.color.divider.default,
    marginHorizontal: theme.sk.spacing.spaceMedium,
  },
}));
